<template>
    <div class="stl-dashboard" :aria-busy="dashboardBusy ? 'true' : 'false'">
        <!-- ===== KPI ===== -->
        <SettlementDashboardSkeleton
            v-if="showKpiSkeleton"
            variant="kpi-row"
            :kpi-count="allKpiCount"
            :gas-layout="isGas"
        />
        <div
            v-else
            class="stl-kpi-row stl-dashboard-kpi"
            :class="{ 'stl-kpi-row--gas': isGas, 'stl-kpi-row--all': isAll }"
        >
            <div class="stl-kpi">
                <div class="stl-kpi-top">
                    <span class="stl-kpi-label">{{ L.totalAmount }}</span>
                    <span v-if="kDelta.text" class="stl-kpi-delta" :class="kDelta.cls" :title="kDelta.text">{{
                        kDelta.short
                    }}</span>
                </div>
                <div class="stl-kpi-bottom">
                    <div class="stl-kpi-value-group">
                        <div class="stl-kpi-value">{{ kCompact.primary }}</div>
                        <div v-if="kCompact.secondary" class="microcap dim">{{ kCompact.secondary }}원</div>
                    </div>
                    <div v-if="amountSparkSeries.length >= 2" class="stl-kpi-spark">
                        <SparkLineChart :values="amountSparkSeries" />
                    </div>
                </div>
            </div>
            <div v-for="kpi in allEnergyKpis" :key="kpi.energy" class="stl-kpi stl-kpi--energy">
                <div class="stl-kpi-top">
                    <span class="stl-kpi-label">{{ kpi.label }} 요금</span>
                    <span v-if="kpi.delta.text" class="stl-kpi-delta" :class="kpi.delta.cls" :title="kpi.delta.text">{{
                        kpi.delta.short
                    }}</span>
                </div>
                <div class="stl-kpi-bottom">
                    <div class="stl-kpi-value-group">
                        <div class="stl-kpi-value">{{ kpi.compact.primary }}</div>
                        <div v-if="kpi.compact.secondary" class="microcap dim">{{ kpi.compact.secondary }}원</div>
                    </div>
                    <div v-if="kpi.spark.length >= 2" class="stl-kpi-spark">
                        <SparkLineChart :values="kpi.spark" :color="energySparkColor(kpi.energy)" />
                    </div>
                </div>
            </div>
            <div v-if="!isAll" class="stl-kpi">
                <div class="stl-kpi-top">
                    <span class="stl-kpi-label">총 사용량 ({{ usageUnitLabel }})</span>
                    <span
                        v-if="usageDelta.text"
                        class="stl-kpi-delta"
                        :class="usageDelta.cls"
                        :title="usageDelta.text"
                        >{{ usageDelta.short }}</span
                    >
                </div>
                <div class="stl-kpi-bottom">
                    <div class="stl-kpi-value-group">
                        <div class="stl-kpi-value">{{ totalUsageText }}</div>
                    </div>
                    <div v-if="usageSparkSeries.length >= 2" class="stl-kpi-spark">
                        <SparkLineChart :values="usageSparkSeries" />
                    </div>
                </div>
            </div>
            <div v-if="!isAll" class="stl-kpi">
                <div class="stl-kpi-top">
                    <span class="stl-kpi-label">세대 사용량 합계</span>
                    <span
                        v-if="subUsageDelta.text"
                        class="stl-kpi-delta"
                        :class="subUsageDelta.cls"
                        :title="subUsageDelta.text"
                        >{{ subUsageDelta.short }}</span
                    >
                </div>
                <div class="stl-kpi-bottom">
                    <div class="stl-kpi-value-group">
                        <div class="stl-kpi-value">{{ subUsageText }}</div>
                    </div>
                    <div v-if="subUsageSparkSeries.length >= 2" class="stl-kpi-spark">
                        <SparkLineChart :values="subUsageSparkSeries" />
                    </div>
                </div>
            </div>
            <div v-if="isElec && !isAll" class="stl-kpi">
                <div class="stl-kpi-top">
                    <span class="stl-kpi-label">공용부 사용량</span>
                    <span
                        v-if="commonUsageDelta.text"
                        class="stl-kpi-delta"
                        :class="commonUsageDelta.cls"
                        :title="commonUsageDelta.text"
                        >{{ commonUsageDelta.short }}</span
                    >
                </div>
                <div class="stl-kpi-bottom">
                    <div class="stl-kpi-value-group">
                        <div class="stl-kpi-value">{{ commonUsageText }}</div>
                    </div>
                    <div v-if="commonSparkSeries.length >= 2" class="stl-kpi-spark">
                        <SparkLineChart :values="commonSparkSeries" />
                    </div>
                </div>
            </div>
        </div>

        <!-- ===== 상태 스트립 ===== -->
        <section
            v-if="!loadingInsight || settlementStatus || qualityData"
            class="stl-status-strip"
            :class="{ 'stl-status-strip--all': isAll }"
        >
            <!-- 정산 진행 -->
            <div class="stl-strip-cell stl-strip-cell--settle">
                <div class="stl-strip-lbl">정산 진행</div>
                <div class="stl-strip-val">
                    <span class="stl-chip" :class="stripStatusChipClass">{{ stripStatusLabel }}</span>
                    <span class="stl-strip-sub">{{ stripConfirmedText }}</span>
                </div>
            </div>
            <span class="stl-strip-divider"></span>
            <!-- 배분 잔차 -->
            <div class="stl-strip-cell stl-strip-cell--remainder">
                <div class="stl-strip-lbl">{{ L.remainder }}</div>
                <div class="stl-strip-val" :class="{ 'stl-strip-val--stack': !!remainderQualityHint }">
                    <div class="stl-strip-val-row">
                        <span class="stl-strip-big" :class="stripRemainderClass">{{ stripRemainderText }}</span>
                        <span v-if="stripRemainder !== null" class="stl-strip-sub">{{ stripRemainderSub }}</span>
                        <span v-if="remainderQualityHint" class="stl-strip-hint">{{
                            `(${remainderQualityHint})`
                        }}</span>
                    </div>
                </div>
            </div>
            <span class="stl-strip-divider"></span>
            <!-- 계량 검증 (단일 에너지) -->
            <template v-if="!isAll">
                <div
                    class="stl-strip-cell stl-strip-cell--verify"
                    :class="{ 'stl-strip-cell--warn-bg': stripVerifyOk === false }"
                >
                    <div class="stl-strip-lbl">{{ verifyStripText }}</div>
                    <div class="stl-strip-val">
                        <span v-if="stripVerifyOk !== null" class="stl-chip" :class="stripVerifyChipClass">{{
                            stripVerifyLabel
                        }}</span>
                        <span v-if="stripVerifyFormula" class="stl-strip-formula">{{ stripVerifyFormula }}</span>
                        <span v-else class="stl-strip-sub">—</span>
                    </div>
                </div>
                <span class="stl-strip-divider"></span>
            </template>
            <!-- 데이터 품질(API) + 편차 이상치(FE) -->
            <div class="stl-strip-cell stl-strip-cell--quality">
                <div class="stl-strip-lbl" title="검침·수집 품질(서버)과 세대 간 편차 이상치(화면)를 구분합니다">
                    데이터 품질
                </div>
                <div class="stl-strip-val">
                    <span
                        v-if="qualityData"
                        class="stl-chip"
                        :class="stripQualityChipClass"
                        :title="'서버 품질 판정: ' + stripQualityLabel"
                        >{{ stripQualityLabel }}</span
                    >
                    <span
                        v-if="outlierRows.size"
                        class="stl-chip warn"
                        :title="'전월 대비·세대 간 편차 기준 이상치 ' + outlierRows.size + '건'"
                        >편차 {{ outlierRows.size }}건</span
                    >
                    <template v-if="outlierShorts.length">
                        <span
                            v-for="name in outlierShorts"
                            :key="name"
                            class="stl-flag-warn"
                            title="편차 이상치 세대"
                            >{{ name }}</span
                        >
                    </template>
                    <span v-else-if="qualityData && !outlierRows.size" class="stl-strip-sub">이상 없음</span>
                    <span v-else-if="!qualityData" class="stl-strip-sub">—</span>
                </div>
            </div>
        </section>
        <SettlementDashboardSkeleton v-else-if="showStripSkeleton" variant="status-strip" :strip-cols="isAll ? 3 : 4" />

        <!-- ===== MAIN (표 + 차트 컬럼) ===== -->
        <div class="stl-dashboard-main">
            <!-- 세대별 정산 결과 표 -->
            <div class="stl-dash-table-col">
                <div class="stl-sec-h">
                    <span class="t">세대별 정산 결과</span>
                    <span v-if="householdCount" class="stl-chip plain">{{ householdCount }}세대</span>
                    <span
                        v-if="outlierRows.size"
                        class="stl-chip warn"
                        :title="'전월 대비·세대 간 편차 기준 이상치 ' + outlierRows.size + '건'"
                        >편차 점검 {{ outlierRows.size }}건</span
                    >
                    <span class="sp"></span>
                    <span v-if="sortedRows.length" class="stl-hint">행 클릭 → 세대 상세</span>
                    <button
                        v-if="reconcileModalData && !loadingInsight && !isAll"
                        type="button"
                        class="stl-btn stl-btn-sm"
                        @click="showReconcileModal = true"
                    >
                        검증 상세
                    </button>
                    <button
                        v-if="showEmptyRunCta"
                        type="button"
                        class="stl-btn stl-btn-primary stl-btn-sm"
                        @click="$emit('open-run')"
                    >
                        정산 실행
                    </button>
                </div>
                <div class="stl-dash-table-body">
                    <SettlementDashboardSkeleton
                        v-if="showMatrixSkeleton"
                        variant="table"
                        :rows="6"
                        :cols="isAll ? energyColumns.length + 2 : showAreaColumn ? 5 : 4"
                    />
                    <div v-else-if="!sortedRows.length" class="stl-empty">
                        <p>
                            해당 월 정산 데이터가 없습니다. 우측 상단 <strong>정산 실행</strong>으로 요금을
                            계산·확정하세요.
                        </p>
                    </div>
                    <div v-else class="stl-table-container stl-table-fill">
                        <table class="stl-table stl-table-sortable">
                            <thead>
                                <tr>
                                    <th class="sortable" @click="setSort('roomName')">호 {{ sortMark("roomName") }}</th>
                                    <template v-if="isAll">
                                        <th v-for="e in energyColumns" :key="e" class="num">
                                            {{ energyLabel(e) }}
                                        </th>
                                        <th class="num sortable" @click="setSort('totalAmount')">
                                            합계 {{ sortMark("totalAmount") }}
                                        </th>
                                    </template>
                                    <template v-else>
                                        <th class="num sortable" @click="setSort('usageQty')">
                                            사용량 {{ sortMark("usageQty") }}
                                        </th>
                                        <th class="num sortable" @click="setSort('individualCost')">
                                            {{ L.usageShare }} {{ sortMark("individualCost") }}
                                        </th>
                                        <th
                                            v-if="showAreaColumn"
                                            class="num sortable"
                                            @click="setSort('allocatedCommonCost')"
                                        >
                                            {{ L.areaShare }} {{ sortMark("allocatedCommonCost") }}
                                        </th>
                                        <th class="num sortable" @click="setSort('totalAmount')">
                                            합계 {{ sortMark("totalAmount") }}
                                        </th>
                                    </template>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in sortedRows"
                                    :key="row.sysNodeIdx"
                                    class="clickable-row"
                                    :class="{
                                        'stl-row-selected': selectedRow && selectedRow.sysNodeIdx === row.sysNodeIdx,
                                        'stl-row-outlier': outlierRows.has(row.sysNodeIdx),
                                    }"
                                    @click="openHouseholdModal(row)"
                                >
                                    <td class="ho">
                                        {{ row.roomName }}
                                        <span
                                            v-if="outlierRows.has(row.sysNodeIdx)"
                                            class="stl-flag-warn"
                                            title="전월 대비 또는 세대 간 편차 이상치"
                                            >편차</span
                                        >
                                    </td>
                                    <template v-if="isAll">
                                        <td v-for="e in energyColumns" :key="e" class="num">
                                            {{ fmtWon(row.byEnergy?.[e]?.totalAmount) }}
                                        </td>
                                        <td class="num">
                                            <strong>{{ fmtWon(row.totalAmount) }}</strong>
                                            <div v-if="compareDeltaText(row, 'totalAmount')" class="microcap dim">
                                                {{ compareDeltaText(row, "totalAmount") }}
                                            </div>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td class="num">
                                            <div class="stl-usage-bar-wrap">
                                                <div class="stl-usage-bar">
                                                    <div
                                                        class="stl-usage-bar-fill"
                                                        :class="{ outlier: outlierRows.has(row.sysNodeIdx) }"
                                                        :style="{ width: usageBarWidth(row) + '%' }"
                                                    ></div>
                                                </div>
                                                <span>{{ fmtUsage(row.usageQty) }}</span>
                                            </div>
                                            <div v-if="compareDeltaText(row, 'usageQty')" class="microcap dim">
                                                {{ compareDeltaText(row, "usageQty") }}
                                            </div>
                                        </td>
                                        <td class="num">
                                            {{ fmtWon(row.individualCost) }}
                                            <div v-if="prevDeltaText(row, 'individualCost')" class="microcap dim">
                                                {{ prevDeltaText(row, "individualCost") }}
                                            </div>
                                        </td>
                                        <td v-if="showAreaColumn" class="num">
                                            {{ fmtWon(row.allocatedCommonCost) }}
                                            <div v-if="prevDeltaText(row, 'allocatedCommonCost')" class="microcap dim">
                                                {{ prevDeltaText(row, "allocatedCommonCost") }}
                                            </div>
                                        </td>
                                        <td class="num">
                                            <strong>{{ fmtWon(row.totalAmount) }}</strong>
                                            <div v-if="compareDeltaText(row, 'totalAmount')" class="microcap dim">
                                                {{ compareDeltaText(row, "totalAmount") }}
                                            </div>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td>합계</td>
                                    <template v-if="isAll">
                                        <td v-for="e in energyColumns" :key="e" class="num">
                                            {{ fmtWon(energyColumnSum(e)) }}
                                        </td>
                                        <td class="num">
                                            <strong>{{ kFormatted || "—" }}</strong>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td class="num">{{ fmtUsage(tableSumUsage) }}</td>
                                        <td class="num">{{ fmtWon(tableSumInd) }}</td>
                                        <td v-if="showAreaColumn" class="num">{{ fmtWon(tableSumArea) }}</td>
                                        <td class="num">
                                            <strong>{{ kFormatted || "—" }}</strong>
                                        </td>
                                    </template>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            <!-- 차트 컬럼 -->
            <div class="stl-dash-chart-col">
                <!-- 청구 추이 -->
                <div class="stl-dash-chart-card">
                    <div class="stl-sec-h">
                        <span class="t">월별 청구 추이</span>
                        <span class="stl-chip plain">원</span>
                    </div>
                    <div class="stl-sec-chart">
                        <SettlementDashboardSkeleton v-if="showTrendSkeleton" variant="chart" />
                        <div v-else-if="amountTrend.labels.length" class="stl-chart-wrap">
                            <MonthlyTrendChart
                                :key="'amt-' + energySource"
                                :labels="amountTrend.labels"
                                :series="amountTrend.series"
                                :highlight-label="ymLabel"
                                :chart-type="isAll ? 'stacked-bar' : 'line'"
                                unit="원"
                            />
                        </div>
                        <div v-else class="stl-empty"><p>표시할 정산 이력이 없습니다.</p></div>
                    </div>
                </div>
                <!-- 사용량 추이 -->
                <div v-if="!isAll" class="stl-dash-chart-card">
                    <div class="stl-sec-h">
                        <span class="t">월별 사용량 추이</span>
                        <span class="stl-chip plain">{{ usageUnitLabel }}</span>
                    </div>
                    <div class="stl-sec-chart">
                        <SettlementDashboardSkeleton v-if="showTrendSkeleton" variant="chart" />
                        <div v-else-if="usageTrend.labels.length" class="stl-chart-wrap">
                            <MonthlyTrendChart
                                :key="'usg-' + energySource"
                                :labels="usageTrend.labels"
                                :series="usageTrend.series"
                                :highlight-label="ymLabel"
                                :unit="usageUnitLabel"
                            />
                        </div>
                        <div v-else class="stl-empty"><p>표시할 사용량 이력이 없습니다.</p></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ===== 검증 상세 모달 ===== -->
        <b-modal
            v-model="showReconcileModal"
            :title="`계량 검증 상세 · ${ymLabel}`"
            size="lg"
            centered
            ok-only
            ok-title="닫기"
            content-class="stl-settlement-portal"
        >
            <div class="settlement-page" style="height: auto; min-height: 200px; overflow: visible">
                <ReconcileHero title="계량 검증" :data="reconcileModalData" style="margin-bottom: 14px" />
                <ReconcileDetailPanel :data="reconcileModalData" :matrix="matrix" />
            </div>
        </b-modal>

        <!-- ===== 세대 상세 모달 (비교 + 일별) ===== -->
        <b-modal
            v-model="showHouseholdModal"
            :title="householdModalTitle"
            size="lg"
            centered
            ok-only
            ok-title="닫기"
            content-class="stl-settlement-portal stl-hh-portal"
            @hidden="resetHouseholdModal"
        >
            <div class="settlement-page stl-household-modal">
                <!-- 탭 (단일 에너지) -->
                <div v-if="!isAll" class="stl-hh-modal-tabs">
                    <div class="stl-seg">
                        <button
                            type="button"
                            :class="{ on: householdModalTab === 'compare' }"
                            @click="householdModalTab = 'compare'"
                        >
                            전월·전년 비교
                        </button>
                        <button
                            type="button"
                            :class="{ on: householdModalTab === 'daily' }"
                            @click="householdModalTab = 'daily'"
                        >
                            일별 사용량
                        </button>
                    </div>
                </div>

                <!-- ALL: 에너지원별 일별 사용량 -->
                <div v-if="isAll" class="stl-hh-modal-body stl-hh-modal-body--all-daily">
                    <div v-if="loadingDailyAll" class="stl-loading">
                        <b-spinner small variant="secondary" /><span>일별 사용량 조회 중...</span>
                    </div>
                    <template v-else>
                        <section v-for="e in energyColumns" :key="e" class="stl-hh-energy-daily">
                            <div class="stl-hh-energy-daily-h">
                                {{ energyLabel(e) }} · 일별 사용량 ({{ energyUsageUnit(e) }})
                            </div>
                            <DailyUsageChart
                                v-if="dailyChartsByEnergy[e] && dailyChartsByEnergy[e].labels.length"
                                :key="'daily-all-' + e + '-' + dailyChartKey"
                                :labels="dailyChartsByEnergy[e].labels"
                                :data="dailyChartsByEnergy[e].data"
                                :unit="energyUsageUnit(e)"
                                :color="energyChartColor(e)"
                            />
                            <div v-else class="stl-empty stl-empty--compact">
                                <p>일별 사용량 데이터가 없습니다.</p>
                            </div>
                        </section>
                    </template>
                </div>

                <!-- 단일 에너지: 비교 탭 -->
                <div v-else-if="householdModalTab === 'compare'" class="stl-hh-modal-body">
                    <div v-if="loadingHouseholdCompare" class="stl-loading">
                        <b-spinner small variant="secondary" /><span>비교 조회 중...</span>
                    </div>
                    <HouseholdCompareSnippet v-else :compare="selectedHouseholdCompare" />
                </div>

                <!-- 단일 에너지: 일별 탭 -->
                <div v-else class="stl-hh-modal-body stl-hh-modal-body--chart">
                    <div v-if="loadingDaily" class="stl-loading">
                        <b-spinner small variant="secondary" /><span>일별 사용량 조회 중...</span>
                    </div>
                    <DailyUsageChart
                        v-else-if="dailyChart.labels.length"
                        :key="dailyChartKey"
                        :labels="dailyChart.labels"
                        :data="dailyChart.data"
                        :unit="dailyChartUnit"
                        :color="energyChartColor(energySource)"
                    />
                    <div v-else class="stl-empty"><p>일별 사용량 데이터가 없습니다.</p></div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import MonthlyTrendChart from "./charts/MonthlyTrendChart.vue";
import DailyUsageChart from "./charts/DailyUsageChart.vue";
import SparkLineChart from "./charts/SparkLineChart.vue";
import HouseholdCompareSnippet from "./HouseholdCompareSnippet.vue";
import ReconcileHero from "./ReconcileHero.vue";
import ReconcileDetailPanel from "./ReconcileDetailPanel.vue";
import SettlementDashboardSkeleton from "./SettlementDashboardSkeleton.vue";
import { buildSiteCompare } from "../utils/settlementCompare.js";
import {
    historyToAmountTrend,
    historyToAmountTrendByEnergy,
    historyEnergyAmountSpark,
    historyToUsageTrend,
    dailyUsageToChart,
} from "../utils/chartAdapters.js";
import { energyChartColor, energySparkColor } from "../utils/chartTheme.js";
import {
    fmtWon,
    fmtUsage,
    fmtPct,
    fmtDate,
    ymToRegYm,
    shiftRegYm,
    currentRegYm,
    fmtWonCompact,
    statusLabel,
    SETTLEMENT_LABELS,
    verifyStripLabel,
    verifyFormulaText,
} from "../utils/settlementFormat.js";

export default {
    name: "SettlementDashboard",
    components: {
        MonthlyTrendChart,
        DailyUsageChart,
        SparkLineChart,
        HouseholdCompareSnippet,
        ReconcileHero,
        ReconcileDetailPanel,
        SettlementDashboardSkeleton,
    },
    props: {
        site: { type: Object, required: true },
        ym: { type: String, required: true },
        energySource: { type: String, default: "Elec" },
    },
    data() {
        return {
            loadingTrend: false,
            loadingMatrix: false,
            loadingInsight: false,
            settlementStatus: null,
            qualityData: null,
            historyRows: [],
            matrix: null,
            prevMatrix: null,
            bundles: null,
            enabledEnergies: [],
            amountTrendRows: [],
            householdCompareMap: {},
            householdCompareLoading: {},
            sortKey: "roomName",
            sortDir: 1,
            householdPtMap: {},
            householdMasterCount: 0,
            selectedRow: null,
            showReconcileModal: false,
            showHouseholdModal: false,
            householdModalTab: "compare",
            householdModalTitle: "",
            loadingHouseholdCompare: false,
            dailyChartKey: 0,
            loadingDaily: false,
            dailyChart: { labels: [], data: [] },
            dailyChartsByEnergy: {},
            loadingDailyAll: false,
        };
    },
    computed: {
        regYm() {
            return ymToRegYm(this.ym);
        },
        showKpiSkeleton() {
            return this.loadingInsight && !this.matrix && !this.historyRows.length;
        },
        showStripSkeleton() {
            return this.loadingInsight && !this.settlementStatus && !this.qualityData;
        },
        showMatrixSkeleton() {
            return this.loadingMatrix && !this.matrix;
        },
        showTrendSkeleton() {
            return this.loadingTrend && !this.historyRows.length;
        },
        dashboardBusy() {
            return this.showKpiSkeleton || this.showStripSkeleton || this.showMatrixSkeleton || this.showTrendSkeleton;
        },

        // ── Summary Bar ──────────────────────────────────────────────
        ymLabel() {
            const raw = String(this.ym || "");
            if (/^\d{4}-\d{2}$/.test(raw)) return raw;
            const s = raw.replace(/\D/g, "");
            if (s.length < 6) return raw;
            return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
        },
        kFormatted() {
            const k = this.matrix?.footer?.kepcoTotalAmount;
            return k != null ? fmtWon(k) : null;
        },
        unitPriceText() {
            const k = this.matrix?.footer?.kepcoTotalAmount;
            const m = this.usageSummary?.masterUsageQty ?? this.matrix?.footer?.usageQty;
            if (!k || !m) return "—";
            return fmtWon(Math.round(Number(k) / Number(m)));
        },
        matrixStatusChipClass() {
            const s = this.settlementStatus?.matrixStatus || this.matrix?.status;
            if (s === "FINAL") return "ok";
            if (s === "DRAFT" || s === "PREVIEW") return "warn";
            return "plain";
        },
        reconcileModalData() {
            if (!this.qualityData) return null;
            const us = this.qualityData.usageSummary || {};
            return {
                ...this.qualityData,
                masterUsageQty: us.masterUsageQty,
                subUsageSumQty: us.subUsageSumQty,
                commonUsageQty: us.commonUsageQty,
                energySource: this.energySource,
                usageUnit: this.isGas ? "Nm3" : this.isWater ? "m3" : "kWh",
            };
        },

        // ── Sparkline series (12개월 history) ─────────────────────────
        amountSparkSeries() {
            const rows = this.isAll ? this.amountTrendRows : this.historyRows;
            return (rows || []).map((r) => Number(r.kepcoTotalAmount || 0));
        },
        usageSparkSeries() {
            return (this.historyRows || []).map((r) => Number(r.masterUsageQty || 0));
        },
        subUsageSparkSeries() {
            return (this.historyRows || []).map((r) => Number(r.subUsageSumQty || 0));
        },
        commonSparkSeries() {
            return (this.historyRows || []).map((r) => Number(r.commonUsageQty || 0));
        },

        // ── 이상치 계산 ───────────────────────────────────────────────
        outlierRows() {
            const rows = this.matrix?.rows || [];
            if (rows.length < 2) return new Set();
            const metricKey = this.isAll ? "totalAmount" : "usageQty";
            const values = rows.map((r) => Number(r[metricKey] || 0));
            const mean = values.reduce((a, b) => a + b, 0) / values.length;
            const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length) || 1;
            const pm = this.prevMap;
            return new Set(
                rows
                    .filter((r) => {
                        const z = (Number(r[metricKey] || 0) - mean) / sd;
                        const prev = pm[r.sysNodeIdx];
                        const jump = prev?.[metricKey]
                            ? Math.abs((Number(r[metricKey]) - Number(prev[metricKey])) / Number(prev[metricKey])) * 100
                            : 0;
                        return z > 1.95 || jump > 48;
                    })
                    .map((r) => r.sysNodeIdx),
            );
        },
        maxUsage() {
            const rows = this.matrix?.rows || [];
            if (!rows.length) return 1;
            return Math.max(1, ...rows.map((r) => Number(r.usageQty || 0)));
        },
        isGas() {
            return this.energySource === "Gas";
        },
        isWater() {
            return this.energySource === "Water";
        },
        isElec() {
            return this.energySource === "Elec";
        },
        isAll() {
            return this.energySource === "ALL";
        },
        allKpiCount() {
            if (!this.isAll) return this.isElec ? 4 : 3;
            return 1 + (this.enabledEnergies.length || this.energyColumns.length || 3);
        },
        allEnergyKpis() {
            if (!this.isAll) return [];
            return this.energyColumns.map((e) => {
                const cur =
                    this.bundles?.[e]?.matrix?.footer?.kepcoTotalAmount ??
                    this.bundles?.[e]?.settlementStatus?.kepcoTotalAmount;
                const prev = this.bundles?.[e]?.prevMatrix?.footer?.kepcoTotalAmount;
                const status = this.bundles?.[e]?.settlementStatus?.matrixStatus || this.bundles?.[e]?.matrix?.status;
                const hasAmount = cur != null;
                return {
                    energy: e,
                    label: this.energyLabel(e),
                    compact: hasAmount ? fmtWonCompact(cur) : { primary: "—", secondary: null },
                    delta: this.buildDelta(cur, prev, (v) => fmtWonCompact(v).primary),
                    spark: historyEnergyAmountSpark(this.historyRows, e),
                    status,
                    statusClass: status === "FINAL" ? "ok" : status === "DRAFT" ? "warn" : "plain",
                    statusLabel: status === "FINAL" ? "확정" : status === "DRAFT" ? "임시저장" : "",
                };
            });
        },
        energyColumns() {
            if (!this.isAll) return [];
            if (this.enabledEnergies.length) return this.enabledEnergies;
            const first = this.matrix?.rows?.[0];
            return first?.byEnergy ? Object.keys(first.byEnergy) : [];
        },
        showAreaColumn() {
            return this.isElec && !this.isAll;
        },
        usageSummary() {
            return this.qualityData?.usageSummary || null;
        },
        prevUsageSummary() {
            return this.qualityData?.prevUsageSummary || null;
        },
        insightQualityLabel() {
            const s = this.qualityData?.status;
            if (s === "BLOCK") return `품질 점검 필요 (${this.qualityData.blockCount || 0})`;
            if (s === "WARN") return `품질 주의 (${this.qualityData.warnCount || 0})`;
            if (s === "OK") return "품질 양호";
            return "";
        },
        insightQualityClass() {
            const s = this.qualityData?.status;
            if (s === "BLOCK") return "block";
            if (s === "WARN") return "warn";
            if (s === "OK") return "ok";
            return "";
        },
        insightMatrixLabel() {
            const s = this.settlementStatus?.matrixStatus || this.matrix?.status;
            return s ? statusLabel(s) : "";
        },
        insightDayCount() {
            return this.qualityData?.expectedDayCount || null;
        },
        insightPeriodText() {
            const q = this.qualityData;
            if (!q?.billingStartAt) return "";
            return `${fmtDate(q.billingStartAt)} ~ ${fmtDate(q.billingEndAt)}`;
        },
        usageUnitLabel() {
            if (this.isGas) return "Nm³";
            if (this.isWater) return "m³";
            return "kWh";
        },
        kCompact() {
            const k = this.matrix?.footer?.kepcoTotalAmount ?? 0;
            return fmtWonCompact(k);
        },
        householdCount() {
            if (this.matrix?.rows?.length) return this.matrix.rows.length;
            return this.householdMasterCount;
        },
        currentHistoryRow() {
            return (this.historyRows || []).find((r) => String(r.regYm) === this.regYm) || null;
        },
        totalUsageText() {
            const v = this.pickUsageField("masterUsageQty");
            if (v === null || v === undefined) return "—";
            return fmtUsage(v);
        },
        subUsageText() {
            const v = this.pickUsageField("subUsageSumQty");
            if (v === null || v === undefined) return "—";
            return fmtUsage(v);
        },
        commonUsageText() {
            const v = this.pickUsageField("commonUsageQty");
            if (v === null || v === undefined) return "—";
            return fmtUsage(v);
        },
        kDelta() {
            if (!this.matrix?.footer && !this.prevMatrix?.footer) return { text: "", short: "", cls: "" };
            return this.buildDelta(
                this.matrix?.footer?.kepcoTotalAmount,
                this.prevMatrix?.footer?.kepcoTotalAmount,
                (v) => fmtWonCompact(v).primary,
            );
        },
        usageDelta() {
            const cur =
                this.usageSummary?.masterUsageQty ??
                this.compareData?.current?.usageQty ??
                this.matrix?.footer?.usageQty;
            const prev = this.prevUsageSummary?.masterUsageQty ?? this.compareData?.compare?.prevMonth?.usageQty;
            if (cur == null && prev == null) return { text: "", short: "", cls: "" };
            return this.buildDelta(cur, prev, (v) => fmtUsage(v));
        },
        subUsageDelta() {
            return this.buildDelta(this.usageSummary?.subUsageSumQty, this.prevUsageSummary?.subUsageSumQty, (v) =>
                fmtUsage(v),
            );
        },
        commonUsageDelta() {
            if (this.isGas || this.isWater) return { text: "", short: "", cls: "" };
            return this.buildDelta(this.usageSummary?.commonUsageQty, this.prevUsageSummary?.commonUsageQty, (v) =>
                fmtUsage(v),
            );
        },
        amountTrend() {
            if (this.isAll) return historyToAmountTrendByEnergy(this.historyRows, this.energyColumns);
            return historyToAmountTrend(this.historyRows, { showCommonPool: this.isElec });
        },
        usageTrend() {
            return historyToUsageTrend(this.historyRows, {
                hideCommon: this.isGas || this.isWater,
            });
        },
        sortedRows() {
            return this.sortRows(this.matrix?.rows || [], this.sortKey, this.sortDir);
        },
        prevMap() {
            const map = {};
            for (const r of this.prevMatrix?.rows || []) {
                map[r.sysNodeIdx] = r;
            }
            return map;
        },
        dailyChartUnit() {
            return this.usageUnitLabel;
        },
        selectedHouseholdCompare() {
            if (!this.selectedRow) return null;
            return this.householdCompareMap[this.selectedRow.sysNodeIdx] || null;
        },
        billingPeriod() {
            if (!this.qualityData?.billingStartAt) return null;
            return {
                billingStartAt: this.qualityData.billingStartAt,
                billingEndAt: this.qualityData.billingEndAt,
            };
        },
        compareData() {
            return buildSiteCompare({
                matrix: this.matrix,
                prevMatrix: this.prevMatrix,
                historyRows: this.historyRows,
                regYm: this.regYm,
            });
        },

        // ── 테이블 합계 (tfoot) ───────────────────────────────────────
        tableSumUsage() {
            return (this.matrix?.rows || []).reduce((s, r) => s + Number(r.usageQty || 0), 0);
        },
        tableSumInd() {
            return (this.matrix?.rows || []).reduce((s, r) => s + Number(r.individualCost || 0), 0);
        },
        tableSumArea() {
            return (this.matrix?.rows || []).reduce((s, r) => s + Number(r.allocatedCommonCost || 0), 0);
        },

        // ── 상태 스트립 ───────────────────────────────────────────────
        stripStatusLabel() {
            const st = this.settlementStatus;
            if (!st) return "미실행";
            if (st.energySource === "ALL" && st.totalEnergies) {
                if (st.finalCount === st.totalEnergies) return `전체 확정 (${st.finalCount}/${st.totalEnergies})`;
                if (st.hasDraft) return `부분 임시저장 (${st.finalCount || 0}/${st.totalEnergies} 확정)`;
            }
            if (st.hasFinal) return statusLabel("FINAL");
            if (st.hasDraft) return statusLabel("DRAFT");
            if (st.kEntered) return SETTLEMENT_LABELS.totalEntered;
            return "미실행";
        },
        stripStatusChipClass() {
            const st = this.settlementStatus;
            if (st?.energySource === "ALL" && st.totalEnergies) {
                if (st.finalCount === st.totalEnergies) return "ok";
                if (st.hasDraft || st.finalCount > 0) return "warn";
                return "plain";
            }
            if (st?.hasFinal) return "ok";
            if (st?.hasDraft) return "warn";
            return "plain";
        },
        stripConfirmedText() {
            const st = this.settlementStatus;
            if (!st) return "실행 전";
            if (st.energySource === "ALL" && st.totalEnergies) {
                if (st.finalCount === st.totalEnergies) {
                    if (st.confirmedAt) {
                        const d = new Date(st.confirmedAt);
                        return `확정일 ${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    }
                    return `${this.energyColumns.map((e) => this.energyLabel(e)).join("·")} 확정`;
                }
                if (st.finalCount > 0 && st.finalCount < st.totalEnergies) {
                    const pending = [];
                    const per = st.perEnergy || {};
                    for (const e of this.energyColumns) {
                        const ps = per[e];
                        if (ps?.matrixStatus !== "FINAL") pending.push(this.energyLabel(e));
                    }
                    if (pending.length) return `${pending.join("·")} 미확정`;
                }
                if (st.hasDraft) return "미확정 · 확정 대기";
                return "실행 전";
            }
            if (st.hasFinal && st.confirmedAt) {
                const d = new Date(st.confirmedAt);
                return `확정일 ${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            }
            if (st.hasDraft) return "미확정 · 확정 대기";
            return "실행 전";
        },
        stripRemainder() {
            return this.settlementStatus?.floorRemainder ?? null;
        },
        stripRemainderText() {
            const r = this.stripRemainder;
            if (r === null) return "—";
            if (r === 0) return "0원";
            return fmtWon(r);
        },
        stripRemainderSub() {
            const r = this.stripRemainder;
            if (r === null) return "";
            return r === 0 ? SETTLEMENT_LABELS.remainderZero : SETTLEMENT_LABELS.remainderCheck;
        },
        remainderQualityHint() {
            const needRemainder =
                this.stripRemainder !== null && this.stripRemainderSub === SETTLEMENT_LABELS.remainderCheck;
            const q = this.qualityData?.status;
            if (needRemainder && (q === "WARN" || q === "BLOCK")) {
                return "잔차·품질을 함께 확인하세요";
            }
            return "";
        },
        stripRemainderClass() {
            const r = this.stripRemainder;
            if (r === null) return "";
            return r === 0 ? "stl-strip-num--ok" : "stl-strip-num--warn";
        },
        stripVerifyOk() {
            if (this.isAll) return null;
            const us = this.qualityData?.usageSummary;
            if (!us) return null;
            const m = Number(us.masterUsageQty || 0);
            const s = Number(us.subUsageSumQty || 0);
            const c = Number(us.commonUsageQty || 0);
            if (!m) return null;
            if (this.isGas || this.isWater) return Math.abs(m - s) < 0.01;
            return Math.abs(m - (s + c)) < 0.01;
        },
        stripVerifyLabel() {
            if (this.stripVerifyOk === true) return "일치";
            if (this.stripVerifyOk === false) return "차이";
            return "—";
        },
        stripVerifyChipClass() {
            if (this.stripVerifyOk === true) return "ok";
            if (this.stripVerifyOk === false) return "warn";
            return "plain";
        },
        stripVerifyFormula() {
            const us = this.qualityData?.usageSummary;
            if (!us) return "";
            return verifyFormulaText(us, {
                isGas: this.isGas,
                isWater: this.isWater,
                unit: this.usageUnitLabel,
            });
        },
        verifyStripText() {
            return verifyStripLabel(this.isGas, this.isWater);
        },
        L() {
            return SETTLEMENT_LABELS;
        },
        stripQualityLabel() {
            const q = this.qualityData;
            if (!q) return "—";
            const s = q.status;
            if (s === "BLOCK") return `품질 점검 ${q.blockCount || 0}건`;
            if (s === "WARN") return `품질 주의 ${q.warnCount || 0}건`;
            if (s === "OK") return "품질 양호";
            return "—";
        },
        showEmptyRunCta() {
            if (this.loadingMatrix || this.showMatrixSkeleton) return false;
            return !this.sortedRows.length;
        },
        stripQualityChipClass() {
            const s = this.qualityData?.status;
            if (s === "BLOCK" || s === "WARN") return "warn";
            if (s === "OK") return "ok";
            return "plain";
        },
        outlierShorts() {
            const rows = this.matrix?.rows || [];
            return rows
                .filter((r) => this.outlierRows.has(r.sysNodeIdx))
                .map((r) => r.roomName)
                .slice(0, 6);
        },
    },
    watch: {
        ym: { handler: "refresh" },
        energySource: { handler: "refresh" },
        site: { handler: "onSiteChange", deep: true },
        matrix: {
            handler(val) {
                // 초기·리마운트 immediate(null)는 헤더를 비우지 않음 — 로드 완료 후만 반영
                if (!val) return;
                this.$emit("matrix-status", val.status || null);
                this.$emit("household-count", this.householdCount);
            },
            immediate: true,
        },
        householdCount(count) {
            if (!count) return;
            this.$emit("household-count", count);
        },
    },
    mounted() {
        this.ensureHouseholdPtMap();
        this.refresh();
    },
    methods: {
        fmtWon,
        fmtUsage,
        energyChartColor,
        energySparkColor,
        energyUsageUnit(e) {
            if (e === "Gas") return "Nm³";
            if (e === "Water") return "m³";
            return "kWh";
        },
        ptIdxForEnergy(ptInfo, e) {
            if (e === "Gas") return ptInfo?.gasPtIdx;
            if (e === "Water") return ptInfo?.waterPtIdx;
            return ptInfo?.electricPtIdx;
        },
        billingPeriodForEnergy(e) {
            const q = this.bundles?.[e]?.quality;
            if (q?.billingStartAt) {
                return { billingStartAt: q.billingStartAt, billingEndAt: q.billingEndAt };
            }
            return this.billingPeriod;
        },
        energyLabel(e) {
            if (e === "Gas") return "가스";
            if (e === "Water") return "수도";
            return "전기";
        },
        energyColumnSum(e) {
            return (this.matrix?.rows || []).reduce((s, r) => s + Number(r.byEnergy?.[e]?.totalAmount || 0), 0);
        },
        usageBarWidth(row) {
            const v = Number(row.usageQty || 0);
            return Math.round((v / this.maxUsage) * 100);
        },
        pickUsageField(field) {
            const fromQuality = this.usageSummary?.[field];
            if (fromQuality != null) return fromQuality;

            const fromMatrix = this.matrix?.footer?.[field];
            if (fromMatrix != null) return fromMatrix;

            const hist = this.currentHistoryRow;
            if (hist && hist[field] != null) return parseFloat(hist[field]);

            if (field === "commonUsageQty" && this.isElec) {
                const m = this.pickUsageField("masterUsageQty");
                const s = this.pickUsageField("subUsageSumQty");
                if (m != null && s != null && Number(m) !== Number(s)) {
                    return parseFloat((Number(m) - Number(s)).toFixed(4));
                }
                return null;
            }
            if (field === "masterUsageQty") {
                return this.compareData?.current?.usageQty ?? null;
            }
            return null;
        },
        buildDelta(cur, prev, fmt) {
            if (cur == null || cur === undefined || prev == null || prev === undefined) {
                return { text: "", short: "", cls: "" };
            }
            const c = Number(cur);
            const p = Number(prev);
            if (!p) return { text: "", short: "", cls: "" };
            const pct = ((c - p) / p) * 100;
            const sign = pct > 0 ? "+" : "";
            const short = `전월 ${sign}${pct.toFixed(1)}%`;
            return {
                text: `${short} (${fmt(p)} → ${fmt(c)})`,
                short,
                cls: pct > 0 ? "stl-delta-up" : pct < 0 ? "stl-delta-down" : "stl-delta-flat",
            };
        },
        sortRows(rows, key, dir) {
            const list = [...(rows || [])];
            list.sort((a, b) => {
                const av = a[key];
                const bv = b[key];
                if (typeof av === "string") return dir * String(av).localeCompare(String(bv), "ko");
                return dir * (Number(av || 0) - Number(bv || 0));
            });
            return list;
        },
        async refresh() {
            this.householdCompareMap = {};
            await this.loadDashboardBundle();
        },
        onSiteChange() {
            this.householdPtMap = {};
            this.householdMasterCount = 0;
            this.refresh();
        },
        async loadDashboardBundle() {
            this.loadingTrend = true;
            this.loadingMatrix = true;
            this.loadingInsight = true;
            const to = currentRegYm();
            const from = shiftRegYm(to, -11);
            const base = {
                siteIdx: this.site.siteIdx,
                regYm: this.regYm,
                energySource: this.energySource,
                regYmFrom: from,
                regYmTo: to,
                historyStatus: "ALL",
            };
            const loadLegacy = () => Promise.all([this.loadTrend(), this.loadMatrix(), this.loadInsight()]);
            try {
                const { data } = await backEndApi.settlement.getDashboardBundle(base);
                this.historyRows = Array.isArray(data.history) ? data.history : [];
                this.matrix = data.matrix || null;
                this.prevMatrix = data.prevMatrix || null;
                this.settlementStatus = data.settlementStatus || null;
                this.qualityData = data.quality || null;
                this.bundles = data.bundles || null;
                this.enabledEnergies = data.energies || [];
                this.amountTrendRows = Array.isArray(data.amountTrend) ? data.amountTrend : [];
            } catch (err) {
                console.warn(
                    "[SettlementDashboard] dashboard-bundle 실패, 개별 API로 폴백",
                    err?.response?.status || err?.message,
                );
                await loadLegacy();
            } finally {
                this.loadingTrend = false;
                this.loadingMatrix = false;
                this.loadingInsight = false;
            }
        },
        async loadInsight() {
            this.loadingInsight = true;
            const base = {
                siteIdx: this.site.siteIdx,
                regYm: this.regYm,
                energySource: this.energySource,
            };
            try {
                const [bundleRes, qualityRes] = await Promise.allSettled([
                    backEndApi.settlement.getDashboardBundle({ ...base, includeHistory: false }),
                    backEndApi.settlement.getQuality(base),
                ]);
                if (bundleRes.status === "fulfilled") {
                    this.settlementStatus = bundleRes.value.data?.settlementStatus || null;
                }
                this.qualityData = qualityRes.status === "fulfilled" ? qualityRes.value.data : null;
            } finally {
                this.loadingInsight = false;
            }
        },
        async loadTrend() {
            this.loadingTrend = true;
            try {
                const to = currentRegYm();
                const from = shiftRegYm(to, -11);
                const { data } = await backEndApi.settlement.getHistory({
                    siteIdx: this.site.siteIdx,
                    regYmFrom: from,
                    regYmTo: to,
                    energySource: this.energySource,
                    status: "ALL",
                });
                this.historyRows = Array.isArray(data) ? data : [];
            } catch (err) {
                this.historyRows = [];
            } finally {
                this.loadingTrend = false;
            }
        },
        async loadMatrix() {
            this.loadingMatrix = true;
            try {
                const prevYm = shiftRegYm(this.regYm, -1);
                const [cur, prev] = await Promise.all([
                    backEndApi.settlement.getHistoryMatrix({
                        siteIdx: this.site.siteIdx,
                        regYm: this.regYm,
                        energySource: this.energySource,
                    }),
                    backEndApi.settlement.getHistoryMatrix({
                        siteIdx: this.site.siteIdx,
                        regYm: prevYm,
                        energySource: this.energySource,
                    }),
                ]);
                this.matrix = cur.data;
                this.prevMatrix = prev.data;
            } catch (err) {
                this.matrix = null;
                this.prevMatrix = null;
                this.householdCompareMap = {};
            } finally {
                this.loadingMatrix = false;
            }
        },
        async ensureHouseholdPtMap() {
            if (Object.keys(this.householdPtMap).length) return;
            try {
                const { data } = await backEndApi.settlement.getHouseholds(this.site.siteIdx);
                const list = Array.isArray(data) ? data : [];
                const map = {};
                for (const h of list) {
                    map[h.sysNodeIdx] = {
                        electricPtIdx: h.electricPtIdx,
                        gasPtIdx: h.gasPtIdx,
                        waterPtIdx: h.waterPtIdx,
                    };
                }
                this.householdPtMap = map;
                this.householdMasterCount = list.length;
                this.$emit("household-count", this.householdCount);
            } catch (err) {
                this.householdPtMap = {};
                this.householdMasterCount = 0;
            }
        },
        async openHouseholdModal(row) {
            this.selectedRow = row;
            this.householdModalTitle = `${row.roomName} · ${this.regYm.slice(0, 4)}-${this.regYm.slice(4, 6)}`;
            this.showHouseholdModal = true;
            await this.ensureHouseholdPtMap();

            if (this.isAll) {
                this.dailyChartsByEnergy = {};
                await this.loadDailyUsageAll(row);
                return;
            }

            this.householdModalTab = "compare";
            if (!this.householdCompareMap[row.sysNodeIdx]) {
                this.loadingHouseholdCompare = true;
                try {
                    const { data } = await backEndApi.settlement.getCompare({
                        siteIdx: this.site.siteIdx,
                        regYm: this.regYm,
                        energySource: this.energySource,
                        sysNodeIdx: row.sysNodeIdx,
                    });
                    this.$set(this.householdCompareMap, row.sysNodeIdx, data);
                } catch (err) {
                    /* ignore */
                } finally {
                    this.loadingHouseholdCompare = false;
                }
            }
            this.dailyChart = { labels: [], data: [] };
            await this.loadDailyUsage(row);
        },
        resetHouseholdModal() {
            this.selectedRow = null;
            this.dailyChart = { labels: [], data: [] };
            this.dailyChartsByEnergy = {};
            this.loadingDaily = false;
            this.loadingDailyAll = false;
            this.loadingHouseholdCompare = false;
        },
        async loadDailyUsageAll(row) {
            const ptInfo = this.householdPtMap[row.sysNodeIdx];
            const energies = this.energyColumns;
            this.loadingDailyAll = true;
            this.dailyChartsByEnergy = {};
            try {
                const entries = await Promise.all(
                    energies.map(async (e) => {
                        const ptIdx = this.ptIdxForEnergy(ptInfo, e);
                        const period = this.billingPeriodForEnergy(e);
                        if (!ptIdx || !period?.billingStartAt || !period?.billingEndAt) {
                            return [e, { labels: [], data: [] }];
                        }
                        const sDt = String(period.billingStartAt).slice(0, 8);
                        const eDt = String(period.billingEndAt).slice(0, 8);
                        const { data } = await backEndApi.summary.searchSummaryByTimeUnit(ptIdx, 3, sDt, eDt);
                        return [e, dailyUsageToChart(Array.isArray(data) ? data : [])];
                    }),
                );
                const charts = {};
                for (const [e, chart] of entries) charts[e] = chart;
                this.dailyChartsByEnergy = charts;
                this.dailyChartKey += 1;
            } catch (err) {
                this.dailyChartsByEnergy = {};
            } finally {
                this.loadingDailyAll = false;
            }
        },
        async loadDailyUsage(row) {
            const ptInfo = this.householdPtMap[row.sysNodeIdx];
            const ptIdx = this.ptIdxForEnergy(ptInfo, this.energySource);
            const period = this.billingPeriod;
            if (!ptIdx || !period?.billingStartAt || !period?.billingEndAt) {
                this.dailyChart = { labels: [], data: [] };
                return;
            }
            this.loadingDaily = true;
            try {
                const sDt = String(period.billingStartAt).slice(0, 8);
                const eDt = String(period.billingEndAt).slice(0, 8);
                const { data } = await backEndApi.summary.searchSummaryByTimeUnit(ptIdx, 3, sDt, eDt);
                this.dailyChart = dailyUsageToChart(Array.isArray(data) ? data : []);
                this.dailyChartKey += 1;
            } catch (err) {
                this.dailyChart = { labels: [], data: [] };
            } finally {
                this.loadingDaily = false;
            }
        },
        setSort(key) {
            if (this.sortKey === key) this.sortDir *= -1;
            else {
                this.sortKey = key;
                this.sortDir = key === "roomName" ? 1 : -1;
            }
        },
        sortMark(key) {
            if (this.sortKey !== key) return "";
            return this.sortDir > 0 ? "▲" : "▼";
        },
        compareDeltaText(row, field) {
            return this.prevDeltaText(row, field);
        },
        prevDeltaText(row, field) {
            const prev = this.prevMap[row.sysNodeIdx];
            if (!prev) return "";
            const cur = Number(row[field] || 0);
            const p = Number(prev[field] || 0);
            if (!p) return "";
            const pct = ((cur - p) / p) * 100;
            return `전월 ${fmtPct(pct)}`;
        },
    },
};
</script>
